import React, { useState } from 'react'

import GenericModal from '../../../../components/GenericModal'
import Dropdown from '../../../../components/Dropdown'
import CsvMappingUpload from '../../../../components/CsvMappingUpload'
import { useAppDispatch } from '../../../../store'
import { getEnrollmentsApi } from '../../../../store/slices/termsSectionsSlice/api'
import { CreateEnrollment } from '../CreateEnrollment'
import { type ApplyFiltersAndDispatchParams } from '../../../../components/CustomTable/types'
import { useParams } from 'react-router-dom'
import { getTerm } from '../../../../store/slices/termsSlice/selectors'
import { notification } from '../../../../components/Notifications'
import { ResetTableButton } from '../../../../components/ResetTableButton'
import { useMutation } from '@tanstack/react-query'
import { resetEnrollmentsByTermId } from '../../../../APIManager/UserManager'
import { type ApiError } from '../../../../models/Common'

interface TopbarActionsProps {
  params?: ApplyFiltersAndDispatchParams
  termId?: string
}

export const TopbarActions: React.FC<TopbarActionsProps> = ({ params, termId }) => {
  const [isUploadModal, setIsUploadModal] = useState<boolean>(false)
  const [isCreateModal, setIsCreateModal] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const { id } = useParams()
  const term = getTerm()

  const closeUploadModal = (): void => {
    setIsUploadModal(false)
  }

  const uploadApi = (): void => {
    setIsUploadModal(true)
  }

  const addManually = (): void => {
    setIsCreateModal(true)
  }

  const menuItems = [
    { text: 'Upload Enrollments (.csv)', action: uploadApi },
    { text: 'Add Manually', action: addManually }
  ]

  const resetTooltip = 'This will remove all student enrollments from this term. Use this action if you want to start over or re-import enrollments. This cannot be undone.'
  const resetModalText = 'Are you sure you want to reset all student enrollments for this term? This action will permanently remove all existing enrollments and cannot be undone.'
  const { mutate: resetEnrollments, isPending: isResetting } = useMutation({
    mutationFn: async () => {
      return await resetEnrollmentsByTermId(termId ?? '')
    },
    onSuccess: (response) => {
      notification(response?.data?.message, { variant: 'success' })
      window.location.reload()
      void dispatch(getEnrollmentsApi({ urlId: id, params: { pageSize: 15 } }))
    },
    onError: (error: ApiError) => {
      notification(error.message || 'Failed to reset enrollments.', { variant: 'error' })
    }
  })

  return (
    <>
      <Dropdown menuItems={menuItems} label='Add Enrollments' />
      <GenericModal
        isOpen={isCreateModal}
        title={'Create Student Enrollment'}
        onClose={() => { setIsCreateModal(false) }}
        maxWidth='sm'
        action={false}
      >
        <CreateEnrollment params={params} handleCloseModal={() => { setIsCreateModal(false) }} />
      </GenericModal>

      <CsvMappingUpload isOpen={isUploadModal} onClose={closeUploadModal} modalTitle="Student Enrollments" csvHeaderEndpoint="/api/users/roles/csv_headers" uploadCsvEndpoint={`/api/users/roles/?is_student=True&term_id=${term?.termId}`} onSuccess={() => {
        notification('Enrollments successfully uploaded', { variant: 'success' })
        void dispatch(getEnrollmentsApi({ urlId: id, params: { pageSize: 15 } }))
      }} />

      <ResetTableButton
        buttonLabel='Reset Enrollments'
        onConfirm={resetEnrollments}
        resetTooltip={resetTooltip}
        resetModalText={resetModalText}
        isLoading={isResetting}
      />
    </>
  )
}
